/**
 * nh-whatsapp-float.js — floating WhatsApp button (sections/nh-whatsapp-float.liquid).
 *
 * On click (synchronously, so the WhatsApp tab is never blocked):
 *   1. builds the message for this page: product + selected variant, cart lines, collection…
 *   2. opens wa.me with it,
 *   3. sends the click to the ops Worker (sendBeacon, for the statistics page),
 *   4. marks the cart with the hidden attribute _nh_wa_click (an order placed afterwards is
 *      then counted as a purchase after a WhatsApp click),
 *   5. publishes the Shopify customer event "nh_whatsapp_click" (for a Meta/GA pixel, optional).
 * The cart is kept current from nh-cart.js (`nh:cart-changed`, window.nhLastCart).
 *
 * Buying buttons are never covered: while the button sits on top of a checkout / add-to-cart /
 * express-pay button (AVOID below), it fades out, and comes back as soon as the page moves on.
 */
(function () {
  if (window.customElements && customElements.get('nh-whatsapp-float')) return;

  const GIFT_KEY = '_nh_gift';
  // Buttons the floating button must never hide (cart page, cart drawer, product page).
  const AVOID = '[name="checkout"], [name="add"], .additional-checkout-buttons, .shopify-payment-button';
  const AVOID_GAP = 8; // px of breathing room around those buttons

  /** Fills [placeholders]; a line whose placeholders are all empty is dropped. */
  function fill(template, values) {
    return String(template || '')
      .split('\n')
      .map((line) => {
        let used = false;
        let empty = true;
        const out = line.replace(/\[(\w+)\]/g, (m, key) => {
          if (!(key in values)) return m;
          used = true;
          const v = values[key] == null ? '' : String(values[key]);
          if (v) empty = false;
          return v;
        });
        return used && empty ? null : out;
      })
      .filter((l) => l !== null)
      .join('\n')
      .trim();
  }

  function money(cents, format) {
    const amount = (Number(cents || 0) / 100).toFixed(2);
    const plain = amount.replace(/\.00$/, '');
    return String(format || '{{amount}} DH').replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) =>
      k.includes('no_decimals') ? plain : k.includes('comma') ? amount.replace('.', ',') : amount
    );
  }

  function cartLines(cart) {
    return (cart.items || [])
      .filter((i) => !(i.properties && i.properties[GIFT_KEY]))
      .map((i) => {
        const v = i.variant_title && i.variant_title !== 'Default Title' ? ` – ${i.variant_title}` : '';
        return `• ${i.quantity}× ${i.product_title}${v}`;
      });
  }

  function selectedVariantId(cfg) {
    const fromUrl = new URLSearchParams(window.location.search).get('variant');
    if (fromUrl && cfg.product.variants[fromUrl]) return fromUrl;
    const input = document.querySelector('main form[action*="/cart/add"] [name="id"]');
    if (input && cfg.product.variants[input.value]) return input.value;
    return String(cfg.product.selected);
  }

  function device() {
    const ua = navigator.userAgent || '';
    if (/iPad|Tablet/i.test(ua)) return 'tablet';
    if (/Mobi|Android|iPhone/i.test(ua)) return 'mobile';
    return 'desktop';
  }

  // First referrer of the visit (an internal page is not a source).
  function landingReferrer() {
    try {
      let r = sessionStorage.getItem('nh_waf_ref');
      if (r === null) {
        const ext = document.referrer && !document.referrer.includes(window.location.host) ? document.referrer : 'direct';
        sessionStorage.setItem('nh_waf_ref', ext);
        r = ext;
      }
      return r;
    } catch (e) {
      return document.referrer || 'direct';
    }
  }

  /** True when box a and box b overlap (with `gap` px of margin). */
  function overlaps(a, b, gap) {
    return b.left < a.right + gap && b.right > a.left - gap && b.top < a.bottom + gap && b.bottom > a.top - gap;
  }

  /** Message + facts about this click. Exported for tests (window.nhWhatsAppFloat). */
  function build(cfg, cart) {
    const values = {
      url: cfg.url || window.location.href,
      collection: cfg.collection || '',
      search: cfg.search || '',
      article: cfg.article || '',
      product: '',
      variant: '',
      price: '',
      cart: '',
      total: '',
    };
    const info = { page: cfg.template, title: cfg.page || '', handle: '', variant: '', cart_items: 0, cart_total: 0 };

    if (cfg.template === 'product' && cfg.product) {
      const id = selectedVariantId(cfg);
      const v = cfg.product.variants[id] || {};
      values.product = cfg.product.title;
      values.variant = v.title && v.title !== 'Default Title' ? ` – ${v.title}` : '';
      values.price = v.price || '';
      info.title = cfg.product.title;
      info.handle = cfg.product.handle;
      info.variant = v.title && v.title !== 'Default Title' ? v.title : '';
    } else if (cfg.template === 'collection') {
      info.title = cfg.collection || info.title;
    } else if (cfg.template === 'search') {
      info.title = cfg.search || info.title;
    } else if (cfg.template === 'article') {
      info.title = cfg.article || info.title;
    }

    if (cart) {
      const lines = cartLines(cart);
      values.cart = lines.join('\n');
      values.total = lines.length ? money(cart.total_price, cfg.moneyFormat) : '';
      info.cart_items = lines.length;
      info.cart_total = lines.length ? Math.round(Number(cart.total_price || 0) / 100) : 0;
      if (cfg.template === 'cart') info.title = lines.length ? `Panier (${lines.length})` : 'Panier vide';
    }

    // Cart page with an empty cart: the generic message reads better.
    const template = cfg.template === 'cart' && !values.cart && cfg.messageDefault ? cfg.messageDefault : cfg.message;
    return { message: fill(template, values), info };
  }

  function track(cfg, info) {
    const payload = Object.assign({}, info, {
      device: device(),
      referrer: landingReferrer(),
      path: window.location.pathname,
      locale: cfg.locale,
      shop: cfg.shop,
    });
    if (cfg.trackingUrl) {
      try {
        const body = JSON.stringify(payload);
        if (!(navigator.sendBeacon && navigator.sendBeacon(cfg.trackingUrl, body))) {
          fetch(cfg.trackingUrl, { method: 'POST', body, keepalive: true, mode: 'no-cors' }).catch(() => {});
        }
      } catch (e) {}
    }
    // Hidden cart attribute: an order placed after this click shows it (counted as a purchase).
    try {
      const f = window.nhFetch || window.fetch;
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      f(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _nh_wa_click: new Date().toISOString() } }),
        keepalive: true,
      }).catch(() => {});
    } catch (e) {}
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish('nh_whatsapp_click', payload);
      }
    } catch (e) {}
  }

  class NhWhatsAppFloat extends HTMLElement {
    connectedCallback() {
      try {
        this.cfg = JSON.parse(this.querySelector('[data-nh-waf-config]').textContent);
      } catch (e) {
        return;
      }
      this.cart = window.nhLastCart || this.cfg.cart;
      this.onCart = (e) => {
        this.cart = e.detail;
        if (this.queueCheck) this.queueCheck();
      };
      document.addEventListener('nh:cart-changed', this.onCart);
      landingReferrer();
      this.link = this.querySelector('.nh-waf__button');
      this.link.addEventListener('click', () => {
        const { message, info } = build(this.cfg, window.nhLastCart || this.cart);
        this.link.href = `https://wa.me/${this.cfg.number}?text=${encodeURIComponent(message)}`;
        track(this.cfg, info);
      });
      if (this.cfg.avoidButtons !== false) this.watchButtons();
    }

    /** Fades the button out while it sits on a checkout / add-to-cart button. */
    watchButtons() {
      this.queued = false;
      this.check = () => {
        this.queued = false;
        const me = this.link.getBoundingClientRect();
        let covered = false;
        if (me.width) {
          for (const el of document.querySelectorAll(AVOID)) {
            if (this.contains(el)) continue;
            const r = el.getBoundingClientRect();
            if (r.width && r.height && overlaps(me, r, AVOID_GAP)) {
              covered = true;
              break;
            }
          }
        }
        this.classList.toggle('nh-waf--away', covered);
      };
      this.queueCheck = () => {
        if (this.queued) return;
        this.queued = true;
        requestAnimationFrame(this.check);
      };
      // capture: Horizon scrolls inside .page-wrapper on desktop, and scroll events don't bubble.
      window.addEventListener('scroll', this.queueCheck, { passive: true, capture: true });
      window.addEventListener('resize', this.queueCheck);
      // Catches what scroll can't see: cart drawer opening, sections re-rendered, late layout.
      this.timer = setInterval(this.queueCheck, 700);
      this.queueCheck();
    }

    disconnectedCallback() {
      document.removeEventListener('nh:cart-changed', this.onCart);
      if (this.queueCheck) {
        window.removeEventListener('scroll', this.queueCheck, { capture: true });
        window.removeEventListener('resize', this.queueCheck);
        clearInterval(this.timer);
      }
    }
  }

  window.nhWhatsAppFloat = { build, fill, money, cartLines, overlaps, AVOID };
  if (window.customElements) customElements.define('nh-whatsapp-float', NhWhatsAppFloat);
})();
